import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCircleChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy } from '@fortawesome/sharp-regular-svg-icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import TrackerChallengeBox from '../../challenges/components/TrackerChallengeBox';
import { Button } from '../../shared/components/button/Button.tsx';
import { activity_list } from '../../shared/components/data.js';
import { useHttpClient } from '../../shared/hooks/http-hook.ts';
import { shouldShowChallengeOnTracker } from '../../shared/util/challenges.js';
import ActivityList from '../components/ActivityList.js';
import GreyToggle from '../components/GreyToggle.js';
import HorizontalSlider from '../components/HorizontalSlider.js';
import Influencers from '../components/Influencers.js';
import Journal from '../components/Journal.js';
import TrackerHeading from '../components/TrackerHeading.js';

function ActivityTracker(props) {
  const pageid = window.location.pathname;
  const healthArea = pageid.split('/')[1];
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(false);
  const [date, setDate] = useState(false);
  const [sliderSteps, setSliderSteps] = useState(0);
  // value is never read; kept as a stable no-op since ActivityList still calls this setter prop
  const setExistingTracker = () => {};
  const [estimateSteps, setEstimateSteps] = useState(true);
  const [estimatedSteps, setEstimatedSteps] = useState(0);
  const [data, setData] = useState(null);
  const [journal, setJournal] = useState(null);
  const [currentTracker, setCurrentTracker] = useState(null);
  const [showWheelchairToggle, setShowWheelchairToggle] = useState(true);
  const [showStepsToggle, setShowStepsToggle] = useState(true);
  const [wheelchair, setWheelchair] = useState(false);
  const [resetLines, setResetLines] = useState(false);
  const challenge = useSelector((state) => state?.challenges?.challenge);

  const sliderScale = {
    distance: [
      { value: 1, label: `0 ${t('T094')}` },
      { value: 2, label: `7.5 ${t('T094')}` },
      { value: 5, label: `14.9 ${t('T094')}` },
    ],
  };

  const exerciseIntoSteps = (exerciseArr) => {
    let steps = 0;
    exerciseArr.forEach((item) => {
      const activity = activity_list.find((actvty) => actvty.id === item.id);
      const durationInSteps = Number(activity.value) * Number(item.duration);
      steps += durationInSteps;
    });
    setEstimatedSteps(steps);
    if (estimateSteps) {
      setSliderSteps(() => steps);
      saveResponse('data', {
        id: 'steps',
        value: steps.toFixed(),
      });
      saveResponse('data', {
        id: 'distance_covered',
        value: Math.round((steps / 1250) * 0.621371 * 10) / 10,
      });
    }
  };

  const getData = async (requestedDate) => {
    if (requestedDate.length > 0) {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/trackers/activity/${requestedDate}`
        );
        if (response && response.tracker?._id) {
          const { tracker } = response;
          setCurrentTracker({
            id: tracker._id,
            trackerDate: tracker.tracker_date.toString().slice(0, 10),
          });
          const trackerData = JSON.parse(tracker.data);
          setData(trackerData);
          setSliderSteps(Number(trackerData.steps));
          setJournal({
            enhancers: tracker.enhancers,
            inhibitors: tracker.inhibitors,
            journal: tracker.journal,
          });
          setIsSaved(true);
          setExistingTracker(true);
        } else {
          setSliderSteps(0);
          setCurrentTracker({
            id: '',
            trackerDate: requestedDate,
          });
          setData({
            activity: [],
            steps: '',
            wheelchair: false,
            distance_covered: '',
          });
          setJournal({
            enhancers: [],
            inhibitors: [],
            journal: '',
          });
        }
      } catch (err) {}
    }
  };

  useEffect(() => {
    setResetLines(true);
    getData(date);
  }, [date]);

  const saveResponse = (location, stat) => {
    if (location === 'journal') {
      setJournal((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    } else {
      setData((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
      if (stat.id === 'activity') {
        // calculate estimated steps
        exerciseIntoSteps(stat.value);
      }
    }
  };

  const sendData = async () => {
    let method = 'POST';
    let url = `${process.env.REACT_APP_BACKEND_URL}/trackers/`;
    if (currentTracker.id !== '') {
      method = 'PUT';
      url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${currentTracker.id}`;
    }
    try {
      const response = await sendRequest(
        url,
        method,
        JSON.stringify({
          trackerDate: date,
          type: 'activity',
          data,
          hidden: false,
          enhancers: journal.enhancers,
          inhibitors: journal.inhibitors,
          journal: journal.journal,
          platform: 'web',
        })
      );
      if (response) {
        setIsSaved(true);
        props.closeTrackers();
      }
    } catch (err) {}
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendData();
  };

  const handleToggleSteps = () => {
    if (estimateSteps) {
      setEstimateSteps(false);
      setSliderSteps(0);
    } else {
      setEstimateSteps(true);
      setSliderSteps(estimatedSteps);
    }
  };

  const handleSliderSteps = (newValue) => {
    setSliderSteps(newValue);
    saveResponse('data', {
      id: 'steps',
      value: Number(newValue).toFixed(),
    });
    saveResponse(
      'data',

      {
        id: 'distance_covered',
        value: Math.round((Number(newValue) / 1250) * 0.621371 * 10) / 10,
      }
    );
    // }
  };

  const closeWheelchairToggle = () => {
    setShowWheelchairToggle(false);
  };

  const closeStepsToggle = () => {
    setShowStepsToggle(false);
  };

  const handleToggleWheelchair = (on) => {
    // use wheelchair estimates and change steps to miles in the horseshoe slider
    setWheelchair(!wheelchair);
    saveResponse('data', {
      id: 'wheelchair',
      value: on,
    });
  };

  return (
    <div
      id="snm-activity"
      className="tracker-container"
      role="region"
      aria-label={`${t('T346')} ${t('tracker.region.title')}`}
    >
      <form className="tracker-form" onSubmit={handleSubmit} noValidate={false}>
        <div className="trackers-inner">
          <div className="back-to-main">
            <button
              className="back-btn"
              aria-label={t('A136')}
              title={t('A136')}
              onClick={props.handleBackToMain}
              type="button"
            >
              <FontAwesomeIcon icon={faCircleChevronLeft} />
            </button>
          </div>
          <TrackerHeading title="T064" setDate={setDate} isSaved={isSaved} />
          {data && (
            <div className="trackers-body" role="group" aria-label={t('modal.tracker.details')}>
              <div className="ind-tracker">
                {showWheelchairToggle && (
                  <GreyToggle
                    questionid="wheelchair"
                    wheelchair={wheelchair}
                    toggleWheelchair={handleToggleWheelchair}
                    closeWheelchairToggle={closeWheelchairToggle}
                    closeButtonLabel={t('trackers.greyToggle.wheelchair.close')}
                    switchButtonLabel={t('T065')}
                  />
                )}
                {shouldShowChallengeOnTracker(healthArea, challenge?.type, challenge?.isActive) && (
                  <TrackerChallengeBox />
                )}
                <fieldset className="ind-tracker no-padding">
                  <legend className="sr-only">{t('T068')}</legend>
                  <div className="tracker-content-heading">
                    <h2 className="title">{t('T068')}</h2>
                    <p className="optional-text">{t('T019')}</p>
                  </div>
                  <ActivityList
                    questionid="activity"
                    data={data}
                    saveResponse={saveResponse}
                    resetLines={resetLines}
                    setResetLines={setResetLines}
                    setExistingTracker={setExistingTracker}
                  />
                </fieldset>
              </div>
              <div className="ind-tracker" role="group" aria-labelledby="steps-title">
                <h2 className="title" id="steps-title">
                  {wheelchair ? t('T550') : t('T072')}{' '}
                  {shouldShowChallengeOnTracker(
                    healthArea,
                    challenge?.type,
                    challenge?.isActive
                  ) && (
                    <div className="tc-badge">
                      <FontAwesomeIcon icon={faTrophy} /> <p>{t('T017')}</p>
                    </div>
                  )}
                </h2>
                <div className="buffer" />
                {showStepsToggle && (
                  <GreyToggle
                    wheelchair={wheelchair}
                    steps
                    toggleSteps={handleToggleSteps}
                    closeStepsToggle={closeStepsToggle}
                    showCloseButton={false}
                    switchButtonLabel={t('trackers.greyToggle.estimateSteps')}
                  />
                )}
                <div role="group" aria-label={t('steps.slider.title')}>
                  <HorizontalSlider
                    values={sliderScale.distance}
                    isActivityTracker
                    wheelchair={wheelchair}
                    sliderSteps={sliderSteps}
                    handleSliderSteps={handleSliderSteps}
                    estimateSteps={estimateSteps}
                    required
                    title={wheelchair ? t('T551') : t('T092')}
                  />
                </div>
                <input
                  type="hidden"
                  name="steps"
                  value={sliderSteps}
                  required
                  aria-label={wheelchair ? t('T550') : t('T072')}
                />
              </div>
              <fieldset className="ind-tracker no-padding">
                <legend
                  className="tracker-content-heading title title-with-border"
                  id="influencers-title-activity"
                >
                  <h2 className="title title-with-border">{t('T091')}</h2>
                  <p className="optional-text">{t('T019')}</p>
                </legend>
                <Influencers
                  activity
                  questionid={['enhancers', 'inhibitors']}
                  data={journal}
                  saveResponse={saveResponse}
                  greenListAriaLabel={t('trackers.influencers.activityMotivators')}
                  redListAriaLabel={t('trackers.influencers.activityBlockers')}
                  titleId="influencers-title-activity"
                />
              </fieldset>
              <div className="ind-tracker" role="group" aria-label={`${t('T346')} - ${t('T018')}`}>
                <Journal
                  placeholder={t('T542')}
                  questionid="journal"
                  data={journal}
                  saveResponse={saveResponse}
                />
              </div>
            </div>
          )}
        </div>
        <div className="buttons">
          <Button onClick={props.closeTrackers} variant="secondary" color="dark" type="button">
            {t('T015')}
          </Button>
          <Button type="submit" icon={{ faIconDefinition: faArrowRight }} iconPosition="right">
            {t('T016')}
          </Button>
        </div>
      </form>
    </div>
  );
}
export default ActivityTracker;
